import React from "react";

import { faGripLines } from "@performyard/fontawesome/pro-regular-svg-icons";
import { AvatarProfile, Chip, Icon, Text, cn } from "@performyard/py-components";
import type { DragHandlers } from "@performyard/py-components/src/components/molecules/SortableOrderedList";
import type { Option } from "@performyard/py-components/src/types/Option";

import type { EmployeeValue } from "../Inputs/SelectEmployee";

// Static relational options for author/signer selection
export const STATIC_EMPLOYEE_OPTIONS: Option<EmployeeValue>[] = [
	{ label: "Employee Being Reviewed", value: { id: "employee-being-reviewed", name: "Employee Being Reviewed" } },
	{ label: "Employee's Manager", value: { id: "employees-manager", name: "Employee's Manager" } },
	{ label: "Other Employee", value: { id: "other-employee", name: "Other Employee" } },
	{ label: "Manager's Manager", value: { id: "managers-manager", name: "Manager's Manager" } },
	{ label: "Contributors", value: { id: "contributors", name: "Contributors" } },
	{ label: "Direct Reports", value: { id: "direct-reports", name: "Direct Reports" } },
];

// Helper to check if an employee ID is a static option
export const isStaticEmployeeOption = (id: string) => STATIC_EMPLOYEE_OPTIONS.some((option) => option.value.id === id);

// Helper to get initials from a name
export const getInitials = (name: string) => {
	const words = name.split(" ");
	if (words.length === 2) {
		return words[0][0] + words[1][0];
	}
	return words[0]?.[0] || "";
};

// Shared component for rendering employee chips
const EmployeeChip = ({ employee, onDismiss }: { employee: EmployeeValue; onDismiss?: () => void }) => {
	const isStatic = isStaticEmployeeOption(employee.id);

	if (isStatic) {
		return (
			<Chip
				label={employee.name}
				color="primary"
				size={24}
				onDismiss={onDismiss}
				icon={
					<div className="-ml-[2px] flex size-5 items-center justify-center rounded-full bg-primary-5 text-[10px] font-medium text-neutral-0">
						{getInitials(employee.name)}
					</div>
				}
			/>
		);
	}

	return (
		<Chip employeeId={employee.id} employeeName={employee.name} color="primary" size={24} onDismiss={onDismiss} />
	);
};

EmployeeChip.displayName = "EmployeeChip";

// Factory function to create ordered item renderers
export const createEmployeeOrderedItemRenderer =
	(readOnly: boolean) =>
	// eslint-disable-next-line react/display-name
	(employee: EmployeeValue, index: number, onRemove: () => void, dragHandlers: DragHandlers, isDragging: boolean) => (
		<div className={cn("flex items-center py-0.5", !readOnly && isDragging && "opacity-50 transition-all")}>
			{!readOnly && (
				<div {...dragHandlers.attributes} {...dragHandlers.listeners} className="flex items-center">
					<Icon icon={faGripLines} size={24} dangerouslySetClassName="cursor-move" />
				</div>
			)}
			<span className="px-1 text-caption font-regular text-neutral-9">{index}.</span>
			<EmployeeChip employee={employee} onDismiss={readOnly ? undefined : onRemove} />
		</div>
	);

// Render function for employee ordered items (editable with drag handles)
export const renderEmployeeOrderedItem = createEmployeeOrderedItemRenderer(false);

// Render function for read-only view (no drag handles, no dismiss buttons)
export const renderEmployeeOrderedItemReadOnly = createEmployeeOrderedItemRenderer(true);

// Render function for dropdown options
export const renderEmployeeDropdownOption = (option: Option<EmployeeValue>) => {
	const isStatic = isStaticEmployeeOption(option.value.id);

	if (isStatic) {
		return (
			<div className="flex items-center gap-2">
				<div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary-5 text-[10px] font-medium text-neutral-0">
					{getInitials(option.value.name)}
				</div>
				<Text size="paragraph-3" color="neutral-9">
					{option.value.name}
				</Text>
			</div>
		);
	}

	return (
		<AvatarProfile
			employeeId={option.value.id}
			employeeName={option.value.name}
			avatarSize={20}
			textSize="paragraph-3"
			noLink
		/>
	);
};
